import { Heart, MessageCircle, Repeat } from "lucide-react"
import { memo, type ReactNode } from "react"
import { highlightMentions, profilePictures } from "../utils/messageUtils"

interface MessageBubbleProps {
  username: string
  message: string
  timestamp: string
  stats?: {
    comments: number
    retweets: number
    likes: number
  }
  isUser?: boolean
  children?: ReactNode
}

export const MessageBubble = memo(function MessageBubble({
  username,
  message,
  timestamp,
  stats = { comments: 1, retweets: 0, likes: 1 },
  isUser = true,
  children,
}: MessageBubbleProps) {
  return (
    <div className="relative w-[96%] sm:w-full mx-auto">
      <div
        className={isUser ? "bg-white/[.062] rounded-[1.1rem] p-4 sm:p-6 backdrop-blur-md" : ""}
        style={
          isUser
            ? {
                background: `
                  rgba(255,255,255,0.025),
                  linear-gradient(20deg, rgba(81,105,197,0.25) 0%, rgba(81,105,197,0.15) 18%, rgba(81,105,197,0.05) 35%, transparent 52%),
                  radial-gradient(ellipse 40% 20% at 50% 0%, rgba(81,105,197,0.3) 0%, rgba(81,105,197,0.2) 40%, transparent 70%),
                  radial-gradient(ellipse 20% 60% at 100% 100%, rgba(168,178,216,0.15) 0%, rgba(168,178,216,0.1) 25%, rgba(168,178,216,0.05) 50%, rgba(168,178,216,0.025) 75%, transparent 95%)
                `,
                boxShadow: "inset 0 0 20px rgba(255, 255, 255, 0.1)",
              }
            : {}
        }
      >
        {/* Glass sheen overlay border for user messages */}
        {isUser && (
          <div
            className="absolute inset-0 rounded-[1.1rem] pointer-events-none"
            style={{
              border: "1px solid rgba(255,255,255,0.11)",
              backgroundImage:
                "linear-gradient(20deg, rgba(81,105,197,0.6) 0%, rgba(81,105,197,0.36) 18%, rgba(81,105,197,0.12) 35%, transparent 52%), radial-gradient(ellipse 40% 20% at 50% 0%, rgba(81,105,197,0.3) 0%, rgba(81,105,197,0.2025) 40%, transparent 70%), radial-gradient(ellipse 20% 60% at 100% 100%, rgba(168,178,216,0.225) 0%, rgba(168,178,216,0.15) 25%, rgba(168,178,216,0.075) 50%, rgba(168,178,216,0.0375) 75%, transparent 95%)",
              backgroundOrigin: "border-box",
              backgroundClip: "border-box",
              WebkitMask: "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            }}
          />
        )}

        {/* Wrapper for Bracky's special container */}
        {!isUser ? children : null}

        {/* Message content */}
        {isUser && (
          <div className="flex items-start space-x-3">
            {/* Avatar */}
            <img
              src={profilePictures[username]}
              alt={`${username} avatar`}
              className="w-9 h-9 sm:w-9 sm:h-9 rounded-full object-cover shrink-0 border border-white/20"
              loading="lazy"
            />
            {/* Message content */}
            <div className="flex-1 min-w-0">
              {/* Username */}
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-white/80 text-[0.9375rem] md:text-base font-bold">{username}</span>
                <span className="text-white/40 text-[0.9375rem] md:text-base">·</span>
                <span className="text-white/40 text-[0.9375rem] md:text-base">{timestamp}</span>
              </div>
              <div className="text-white text-[0.9375rem] sm:text-[1.0625rem] leading-[1.43] whitespace-pre-line wrap-break-word mt-[0.1rem] mb-[0.1rem]">
                {highlightMentions(message)}
              </div>
              {/* Action bar */}
              <div className="flex items-center space-x-4 mt-2 text-white/40 text-xs">
                <div className="flex items-center space-x-1">
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>{stats.comments}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Repeat className="w-3.5 h-3.5" />
                  <span>{stats.retweets}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Heart className="w-3.5 h-3.5" />
                  <span>{stats.likes}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})
